<script setup lang="ts">
    import BaseButton from '@/components/BaseButton.vue';
    import BaseIcon from '@/components/BaseIcon.vue';
    import { postImage } from '@/api/image.api';
    import { t } from '@/translations/i18n';
    import { acceptedImageTypes, maxImageSize } from '@/config';
    import useConfigStore from '@/store/config.store';
    import useProductStore from '@/store/product.store';
    import { GFabricWorld } from '@/guidetti/GFabricWorld';
    import Events from '@/services/Events';
    import { loadAlreadyCreatedFields } from '@/commons/fabricjs/FabricCallbackHelper';
    import { orderElementList } from '@/commons/fabricjs/FabricPositionHelper';
    import useField from '@/composables/useField';
    import FabricCanvasContainer from '@/components/FabricCanvasContainer.vue';
    import { computed, nextTick, onMounted, onUnmounted, ref, useTemplateRef, watch } from 'vue';
    import { storeToRefs } from 'pinia';
    import type { IFabricWorldInitParams } from '@/commons/fabricjs/IFabricWorldInitParams';
    import type { IImageField } from '@/typescript/interfaces/IImageField';
    import type { ITextField } from '@/typescript/interfaces/ITextField';
    import type { FabricImage, FabricText } from 'fabric';

    let fabricWorld: GFabricWorld | null;
    const fabricElement = useTemplateRef<HTMLCanvasElement | null>('fabricElement');

    const { field, warningMessage } = defineProps<{ field: IImageField; warningMessage?: string }>();
    const { setInitialField, resetField } = useField(field.id);

    const emit = defineEmits<{
        imageUpload: [string | undefined, string | undefined, string | undefined];
        close: [void];
    }>();

    const productStore = useProductStore();
    const { isAdmin, fonts, fZoom, uniqueId } = storeToRefs(useConfigStore());

    // Get already existed elements
    const { selectedProductConfiguration } = storeToRefs(productStore);
    const filteredFields = computed(() => {
        return selectedProductConfiguration.value.fields.filter(
            (filteredField) =>
                filteredField.id !== field.id &&
                (filteredField.transform.position.x + filteredField.transform.position.y !== 0 ||
                    (filteredField as IImageField)?.url !== undefined ||
                    (filteredField as ITextField)?.text !== undefined),
        );
    });

    const fabricParams: IFabricWorldInitParams = {
        canvas: null,
        enableRetinaScaling: true,
        includeDefaultValues: false, // do not export default values toJSON (shorter json)
        absolutePan: true,
        selection: false,
        allowTouchScrolling: true,
        allowTouchPan: true,
        uniqueId: uniqueId.value,
        preserveObjectStacking: true,
        renderOnAddRemove: false,
    };

    const imageUrl = ref<string>();
    const imageName = ref<string>();
    const imageUUID = ref<string>();

    const uploadInputRef = useTemplateRef('uploadInput');

    onMounted(() => {
        fabricWorld = new GFabricWorld();
        setInitialField(field);
        imageUrl.value = field.url;
        imageUUID.value = field.uuid;
        imageName.value = field.filename;
        Events.on('image-transformed', updateImageTransformation);
        nextTick().then(() => {
            if (!imageUrl.value || !fabricWorld) {
                return;
            }
            fabricWorld?.init({ ...fabricParams, canvas: fabricElement.value });
            fZoom.value = fabricWorld.fabricCanvas?.getZoom() ?? 1;
            Promise.all([
                loadAlreadyCreatedFields(fabricWorld, filteredFields.value, fonts.value),
                fabricWorld.addImage(imageUrl.value, field, true),
            ]).then(() => {
                if (!fabricWorld) return;
                const fabricList = fabricWorld.fabricCanvas?.getObjects() as (
                    | (FabricText & { id: string })
                    | (FabricImage & { id: string })
                )[];
                const fieldOrder = selectedProductConfiguration.value.fields.map((fieldToOrder) => fieldToOrder.id);
                orderElementList(fabricWorld.fabricCanvas, fabricList, fieldOrder, undefined);
                fabricWorld.addBackgroundColor(selectedProductConfiguration.value.poleColor);
                fabricWorld.stickerAndBrand();
                const group = fabricWorld.addGuides();
                fabricWorld.focusOnElement(field.id);
                fZoom.value = fabricWorld.fabricCanvas?.getZoom() ?? 1;
                // force group forward
                fabricWorld.fabricCanvas?.bringObjectToFront(group);
            });
        });
    });

    onUnmounted(() => {
        fabricWorld?.dispose();
        fabricWorld = null;
        Events.off('image-transformed', updateImageTransformation);
    });

    watch(fZoom, (newValue, oldValue) => {
        if (!fabricWorld) return;
        const zoom = fabricWorld.fabricCanvas?.getZoom();
        fabricWorld.zoomInOut({ zoomRef: fZoom, oldZoom: zoom ?? oldValue });
    });

    const inputAccept = computed(() => {
        return acceptedImageTypes.map((type) => type.extensions).join(',');
    });

    async function onImageChange(e: Event) {
        const { files } = e.target as HTMLInputElement;

        if (!files?.length) {
            // eslint-disable-next-line no-alert
            window.alert(t('no_files'));
            return;
        }

        const file = files[0];
        if (!acceptedImageTypes.some((type) => type.type === file.type)) {
            // eslint-disable-next-line no-alert
            window.alert(t('image_format_error'));
            return;
        }

        if (file.size >= maxImageSize) {
            // eslint-disable-next-line no-alert
            window.alert(t('image_size_error'));
            return;
        }

        try {
            if (isAdmin.value) {
                imageUUID.value = await postImage(file);
            }
            imageUrl.value = URL.createObjectURL(file);
            imageName.value = file.name;
            const fieldCopy = { ...field };
            fieldCopy.url = imageUrl.value;
            productStore.updateField(fieldCopy);
            // next tick to ensure the canvas is ready
            nextTick().then(() => {
                if (!imageUrl.value || !fabricWorld) {
                    return;
                }
                fabricWorld.init({ ...fabricParams, canvas: fabricElement.value });
                Promise.all([
                    loadAlreadyCreatedFields(fabricWorld, filteredFields.value, fonts.value),
                    fabricWorld.addImage(imageUrl.value, field),
                ]).then(() => {
                    if (!fabricWorld) return;
                    const fabricList = fabricWorld.fabricCanvas?.getObjects() as (
                        | (FabricText & { id: string })
                        | (FabricImage & { id: string })
                    )[];
                    const fieldOrder = selectedProductConfiguration.value.fields.map((fieldToOrder) => fieldToOrder.id);
                    orderElementList(fabricWorld.fabricCanvas, fabricList, fieldOrder, undefined);
                    fabricWorld.addBackgroundColor(selectedProductConfiguration.value.poleColor);
                    fabricWorld.stickerAndBrand();
                    const group = fabricWorld.addGuides();
                    // force group forward
                    fabricWorld.fabricCanvas?.bringObjectToFront(group);
                    fZoom.value = fabricWorld.fabricCanvas?.getZoom() ?? 1;
                    emit('imageUpload', imageUrl.value, imageName.value, imageUUID.value);
                });
            });
        } catch (error) {
            Events.emit('call-toaster', { title: t('error_title'), body: error as string, type: 'error' });
        }
    }

    function openImageUpload() {
        uploadInputRef.value?.click();
    }

    function close() {
        emit('close');
    }

    function validate() {
        emit('imageUpload', imageUrl.value, imageName.value, imageUUID.value);
        close();
    }

    function cancel() {
        resetField();
        close();
    }

    function updateImageTransformation(fieldEdited: Partial<IImageField>) {
        const fieldToEdit = { ...field };
        if (fieldToEdit.id !== fieldEdited.id) return;
        if (fieldEdited.transform) {
            fieldToEdit.transform = fieldEdited.transform;
        }
        fieldToEdit.frameSize = fieldEdited.frameSize;
        fieldToEdit.hasQualityWarning = fieldEdited.hasQualityWarning;
        productStore.updateField(fieldToEdit);
    }
</script>

<template>
    <div class="flex h-full w-full flex-col items-center justify-around gap-2 bg-white p-4">
        <div
            class="flex w-full grow flex-col"
            :class="[imageUrl && 'bg-bg border-border overflow-hidden border']"
        >
            <div
                v-if="imageUrl && warningMessage"
                class="absolute z-10 flex w-full flex-row items-center gap-4 bg-alert-secondary p-2 text-alert-primary"
            >
                <span class="flex h-6 w-6 flex-none items-center justify-center rounded-full bg-alert-primary font-bold text-white">
                    !
                </span>
                <p>{{ warningMessage }}</p>
            </div>

            <FabricCanvasContainer v-if="imageUrl">
                <canvas
                    ref="fabricElement"
                    class="h-full"
                    title="image canvas"
                />
            </FabricCanvasContainer>
            <div
                v-else
                class="flex h-full w-full flex-col items-center justify-center gap-10"
            >
                <BaseIcon
                    name="PhotoIcon"
                    class="h-28 w-28 text-bg-primary"
                    type="outlined"
                />
                <span class="w-full text-center">
                    <p class="font-bold">{{ $t('allowed_formats_label') }}</p>
                    <p>{{ $t('allowed_formats') }}</p>
                    <p>{{ $t('max_image_size') }}</p>
                </span>
                <BaseButton
                    class="h-16 w-64 items-center justify-center"
                    variant="filled"
                    @click="openImageUpload"
                >
                    {{ $t('upload_input_label') }}
                </BaseButton>
                <label
                    class="sr-only"
                    for="uploadInput"
                >
                    {{ $t('upload_input_label') }}
                </label>
                <input
                    id="uploadInput"
                    ref="uploadInput"
                    hidden
                    type="file"
                    :accept="inputAccept"
                    @change="onImageChange"
                />
            </div>
        </div>
        <div class="flex w-full flex-row gap-2">
            <BaseButton
                :class="[imageUrl && 'h-10', !imageUrl && 'h-16', 'flex-1']"
                @click="cancel"
            >
                {{ $t('cancel') }}
            </BaseButton>
            <BaseButton
                v-if="imageUrl"
                variant="filled"
                color="secondary"
                class="min-h-10 flex-1"
                @click="validate"
            >
                {{ $t('validate') }}
            </BaseButton>
        </div>
    </div>
</template>
